import { CalendarIcon, ClockIcon, UserIcon } from "@xola/icons";
import { ComboBox, Label } from "@xola/ui-kit";
import clsx from "clsx";
import {
    DateFormat,
    formatDate,
    isEquipmentItem,
    isExperiencePurchaseItem,
    isMistPurchase,
    isStoreCredit,
} from "common";
import React from "react";
import { components, ControlProps } from "react-select";
import { Experience, ExperiencePurchaseItem, Product, Purchase, PurchaseItem } from "types";
import { useSeller } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import { isPackage } from "../../models/Purchase";
import { CreatePurchaseItemState } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";

interface PurchaseItemSelectWrapperProps {
    purchase: Purchase;
    item: PurchaseItem | null;
    product: Product;
    onChange: (item: PurchaseItem | ExperiencePurchaseItem) => void;

    /** (Optional) Purchase items that need to be displayed in the product selector. If not provided, all the items from purchase would be displayed */
    items?: PurchaseItem[];
}

export const PurchaseItemSelectWrapper = ({
    item,
    items,
    purchase,
    product,
    onChange,
}: PurchaseItemSelectWrapperProps) => {
    const isSupportedPurchaseItem = item && !isStoreCredit(purchase) && !isPackage(product);
    if (!isSupportedPurchaseItem || !isMistPurchase(purchase)) {
        return null;
    }

    return (
        <>
            <Label>Select Product</Label>
            <div className="mb-4 w-full">
                <PurchaseItemSelect
                    items={items ?? purchase.items}
                    value={item}
                    className="flex-1"
                    onChange={(item) => onChange(item)}
                />
            </div>
        </>
    );
};

interface PurchaseItemSelectProps {
    onChange: (item: PurchaseItem) => void;
    items: PurchaseItem[] | CreatePurchaseItemState[];
    value: PurchaseItem;
    className?: string;
}

export const PurchaseItemSelect = ({ items, value, onChange, className }: PurchaseItemSelectProps) => {
    if (items.length === 1) {
        // We don't need to show the selector because there is one item only
        return null;
    }

    return (
        <ComboBox
            value={value}
            options={items.map((item) => item)}
            getOptionValue={(option: any) => option.id}
            getOptionLabel={(option: any) => option.name}
            components={{ Option, Control }}
            isDisabled={items.length <= 1}
            menuPortalTarget={document.body}
            styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 99 }),
                valueContainer: (base: any) => ({ ...base, padding: 0, background: "white" }),
                control: (base: any) => ({
                    ...base,
                    boxShadow: "none",
                    "&:hover": { background: "white", cursor: "pointer" },
                }),
            }}
            className={className}
            onChange={onChange}
        />
    );
};

const Control = ({ selectProps, children, ...props }: ControlProps<PurchaseItem, false>) => {
    const PurchaseItemOptionValue =
        selectProps.value && "arrivalDate" in selectProps.value
            ? ExperiencePurchaseItemOptionValue
            : DefaultPurchaseItemOptionValue;

    return (
        <components.Control selectProps={selectProps} {...props}>
            <div className="flex w-full">
                <PurchaseItemOptionValue purchaseItem={selectProps.value as PurchaseItem} />
                {children}
            </div>
        </components.Control>
    );
};

interface OptionProps {
    isSelected: boolean;
    isFocused: boolean;
    data: PurchaseItem;
    innerRef: any;
    innerProps: any;
}

const Option = ({ data, isSelected, isFocused, innerRef, innerProps }: OptionProps) => {
    const PurchaseItemOptionValue = isExperiencePurchaseItem(data)
        ? ExperiencePurchaseItemOptionValue
        : DefaultPurchaseItemOptionValue;
    return (
        <div
            ref={innerRef}
            className={clsx(
                "flex items-center border-b border-gray-light pl-2",
                isSelected && "bg-primary-lighter text-white",
                !isSelected && isFocused && "bg-primary-lighter",
            )}
            {...innerProps}
        >
            <PurchaseItemOptionValue purchaseItem={data} />
        </div>
    );
};

interface PurchaseItemOptionValueProps {
    purchaseItem: PurchaseItem;
}

const ExperiencePurchaseItemOptionValue = ({ purchaseItem: item }: PurchaseItemOptionValueProps) => {
    const seller = useSeller();
    const { data: experience } = useResource<Experience>(["products", item.product.id]);
    const arrivalDateTime = "arrivalDateTime" in item ? item.arrivalDateTime.tz(seller.timezoneName) : null;

    return (
        <div className="flex w-full cursor-pointer items-center p-2">
            <ProductImage product={item.product} size="small" />

            <div className="w-full flex-1 leading-5">
                <NoTranslate
                    as="div"
                    className="text-base font-bold text-black"
                    data-testid="purchase-item-summary-tile-experience"
                >
                    {experience?.name}
                </NoTranslate>
                <div className="flex space-x-3 text-gray-darker">
                    {arrivalDateTime ? (
                        <>
                            <span>
                                <CalendarIcon className="left-[-1.5px]" />
                                <NoTranslate data-testid="purchase-item-summary-tile-arrival-date">
                                    {arrivalDateTime.format("ddd ll")}
                                </NoTranslate>
                            </span>
                            {"arrivalTime" in item && (
                                <span data-testid="purchase-item-summary-tile-arrival-time">
                                    <ClockIcon className="mr-0.5" />
                                    <NoTranslate>{formatDate(arrivalDateTime, DateFormat.TIME)}</NoTranslate>
                                </span>
                            )}
                        </>
                    ) : null}
                    <div data-testid="purchase-item-summary-tile-guests-count">
                        <UserIcon className="mr-0.5" />
                        <NoTranslate>{isEquipmentItem(item) ? item.quantity : item.guests.length}</NoTranslate>{" "}
                        {isEquipmentItem(item) ? "Units" : "Guests"}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DefaultPurchaseItemOptionValue = ({ purchaseItem: item }: PurchaseItemOptionValueProps) => {
    const { data: product } = useResource<Product>(["products", item.product.id]);

    return (
        <div className="flex w-full cursor-pointer items-center p-2">
            <ProductImage product={product ?? item.product} object={item.object} size="small" />

            <div className="w-full flex-1 leading-5">
                <NoTranslate
                    as="div"
                    className="text-base font-bold text-black"
                    data-testid="purchase-item-summary-tile-experience"
                >
                    {item?.name}
                </NoTranslate>
            </div>
        </div>
    );
};
